import { useState } from 'react';
import { ActivityIndicator, Modal, Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { theme } from '@/constants/theme';
import BorderGlow from '@/components/effects/BorderGlow';
import { signInWithGoogle } from '@/utils/supabase';

const PRIMARY = theme.colors.primary;

interface AuthPopupProps {
  visible: boolean;
  onClose: () => void;
  returnTo?: string;
  allowDismiss?: boolean;
}

const VALUE_POINTS = [
  'שמירת מסלול הלמידה האישי שלך מהשלב הנוכחי',
  'מעקב התקדמות וחזרה מדויקת לאותה נקודה בכל זמן',
  'שמירת תרגול, תיקון טעויות ותוצאות מצב בחינה במקום אחד',
];

export function AuthPopup({
  visible,
  onClose,
  returnTo = '/learn/path/create',
  allowDismiss = true,
}: AuthPopupProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('kp_return_to', returnTo);
      } catch {
        // no-op
      }
    }

    const timeout = setTimeout(() => {
      setLoading(false);
      setError('ההתחברות לקחה יותר מדי זמן. נסה שוב.');
    }, 12_000);

    try {
      const result = await signInWithGoogle();
      if (!result.success) {
        clearTimeout(timeout);
        setError('לא הצלחנו להתחבר עם Google. נסה שוב.');
        setLoading(false);
      }
    } catch {
      clearTimeout(timeout);
      setError('שגיאה בהתחברות. נסה שוב.');
      setLoading(false);
    }
  };

  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={allowDismiss ? onClose : undefined}
      statusBarTranslucent
    >
      <View style={s.overlay}>
        <Pressable
          onPress={(event) => event.stopPropagation?.()}
          accessibilityLabel="שמירת ההתקדמות והתחברות"
          style={s.shell}
        >
          <BorderGlow>
            <View style={s.card}>
              <View style={s.pill}>
                <Text style={s.pillText}>שלב אחרון לפני יצירת המסלול</Text>
              </View>

              <Text style={s.title}>שמור את מסלול הלמידה שלך והמשך בדיוק מאותה נקודה</Text>
              <Text style={s.subtitle}>
                התחברות מהירה עם Google שומרת את כל מה שהגדרת עכשיו ופותחת את חוויית הלמידה המלאה.
              </Text>

              <View style={s.featureList}>
                {VALUE_POINTS.map((point) => (
                  <View key={point} style={s.featureItem}>
                    <View style={s.featureDot} />
                    <Text style={s.featureText}>{point}</Text>
                  </View>
                ))}
              </View>

              {!!error && <Text style={s.errorText}>{error}</Text>}

              <Pressable
                style={[s.googleBtn, loading && { opacity: 0.75 }]}
                onPress={handleGoogleSignIn}
                disabled={loading}
                accessibilityRole="button"
                accessibilityLabel="המשך עם Google"
              >
                {loading ? (
                  <ActivityIndicator color="#0F172A" />
                ) : (
                  <>
                    <View style={s.googleMark}>
                      <Text style={s.googleMarkText}>G</Text>
                    </View>
                    <Text style={s.googleBtnText}>המשך עם Google</Text>
                  </>
                )}
              </Pressable>

              <Text style={s.helperText}>ההתחברות משמשת לשמירת התקדמות בלבד. ללא פרסום וללא ספאם.</Text>

              {allowDismiss && (
                <Pressable style={s.secondaryBtn} onPress={onClose} disabled={loading}>
                  <Text style={s.secondaryBtnText}>חזרה למסך הקודם</Text>
                </Pressable>
              )}
            </View>
          </BorderGlow>
        </Pressable>
      </View>
    </Modal>
  );
}

const s = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(4, 7, 20, 0.58)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
    paddingVertical: 28,
  },
  shell: { width: '100%', maxWidth: 560 },
  card: {
    borderRadius: 28,
    backgroundColor: '#121A34',
    paddingHorizontal: 24,
    paddingVertical: 26,
    gap: 14,
  },
  pill: {
    alignSelf: 'flex-end',
    backgroundColor: 'rgba(99, 102, 241, 0.18)',
    borderColor: 'rgba(149, 157, 255, 0.42)',
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 12,
    paddingVertical: 7,
  },
  pillText: { color: '#CFD6FF', fontFamily: theme.fonts.medium, fontSize: 12 },
  title: {
    color: '#FFFFFF',
    fontFamily: theme.fonts.bold,
    fontSize: 30,
    lineHeight: 38,
    textAlign: 'right',
  },
  subtitle: {
    color: '#C4CCE9',
    fontFamily: theme.fonts.regular,
    fontSize: 15,
    lineHeight: 24,
    textAlign: 'right',
  },
  featureList: { gap: 10, marginTop: 4 },
  featureItem: {
    borderWidth: 1,
    borderColor: '#34406F',
    backgroundColor: '#182043',
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
  },
  featureDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    backgroundColor: PRIMARY,
  },
  featureText: {
    color: '#E2E8FF',
    fontFamily: theme.fonts.medium,
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'right',
    flex: 1,
  },
  errorText: {
    color: '#FCA5A5',
    fontFamily: theme.fonts.medium,
    textAlign: 'right',
    marginTop: 2,
  },
  googleBtn: {
    borderRadius: 14,
    backgroundColor: '#F8FAFC',
    minHeight: 56,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginTop: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  googleMark: {
    width: 24,
    height: 24,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#D0D7E6',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  googleMarkText: {
    color: '#2563EB',
    fontFamily: theme.fonts.bold,
    fontSize: 14,
  },
  googleBtnText: {
    color: '#0F172A',
    fontFamily: theme.fonts.bold,
    fontSize: 18,
  },
  helperText: {
    color: '#98A4CF',
    fontFamily: theme.fonts.regular,
    fontSize: 12,
    textAlign: 'center',
  },
  secondaryBtn: {
    alignSelf: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    opacity: 0.9,
  },
  secondaryBtnText: {
    color: '#B8C1E8',
    fontFamily: theme.fonts.medium,
    fontSize: 13,
    textDecorationLine: 'underline',
  },
});
